/**
 * FreeDeliveryProgress — "Add ₹X more for free delivery" progress strip.
 *
 * Renders three states:
 *   • not eligible yet  — primary bar + savings nudge
 *   • exactly eligible  — success bar + celebratory line
 *   • over threshold    — bar full, line collapses to "Free delivery unlocked!"
 *
 * Threshold defaults to ₹299 platform-wide; pass `threshold` to override per store.
 */
import React from 'react';
import { css } from '../../design';

export interface FreeDeliveryProgressProps {
  subtotal: number;
  threshold?: number;
  /** Optional CTA when user is short of the threshold. */
  onShopMore?: () => void;
  /** Hide the helper text under the bar. */
  compact?: boolean;
}

const FreeDeliveryProgress: React.FC<FreeDeliveryProgressProps> = ({
  subtotal,
  threshold = 299,
  onShopMore,
  compact,
}) => {
  const pct = Math.min(100, Math.round((subtotal / threshold) * 100));
  const remaining = Math.max(0, threshold - subtotal);
  const reached = remaining === 0;

  return (
    <div
      style={{
        background: reached ? css.color.successSoft : css.color.surface2,
        border: `1px solid ${reached ? css.color.primary : css.color.border}`,
        borderRadius: 12,
        padding: '10px 12px',
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 13, fontWeight: 700, color: css.color.text }}>
        <span>
          {reached ? (
            <>🎉 Free delivery unlocked!</>
          ) : (
            <>Add <span style={{ color: css.color.primary }}>₹{remaining}</span> more for free delivery</>
          )}
        </span>
        {!reached && onShopMore && (
          <button
            type="button"
            onClick={onShopMore}
            style={{
              background: 'transparent',
              border: 'none',
              color: css.color.primary,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Add items →
          </button>
        )}
      </div>
      <div
        style={{
          height: 8,
          width: '100%',
          background: '#FFFFFF',
          border: `1px solid ${css.color.border}`,
          borderRadius: 999,
          overflow: 'hidden',
        }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={threshold}
        aria-valuenow={Math.min(subtotal, threshold)}
        aria-label="Progress toward free delivery"
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${css.color.primary}, ${css.color.accent})`,
            transition: 'width 400ms ease',
          }}
        />
      </div>
      {!compact && !reached && (
        <div style={{ marginTop: 6, fontSize: 11, color: css.color.textMuted }}>
          Free delivery on orders above ₹{threshold}.
        </div>
      )}
    </div>
  );
};

export default FreeDeliveryProgress;

